import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { GoogleLogin } from '@react-oauth/google';
import authService from '../../api/auth.service';
import toast from 'react-hot-toast';
import { fetchWishlist } from '../../redux/slice/wishlistSlice';
import formService from '../../api/form.data.service';

export const LoginForm = ({ onShowSignup }) => {
  const dispatch = useDispatch();
  const [loginMethod, setLoginMethod] = useState('email');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showOTPVerification, setShowOTPVerification] = useState(false);
  const [timeLeft, setTimeLeft] = useState(600);
  const [otp, setOtp] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    phoneNumber: '',
    password: '',
    newPassword: '',
    confirmPassword: '',
    showResetPassword: false,
  });
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  const eventFormData = useSelector((state) => state.form);

  useEffect(() => {
    let timer;
    if (showOTPVerification && timeLeft > 0) {
      timer = setInterval(() => setTimeLeft((prev) => prev - 1), 1000);
    }
    return () => clearInterval(timer);
  }, [showOTPVerification, timeLeft]);

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const saveEventFormData = async () => {
    if (eventFormData && Object.keys(eventFormData).length > 0) {
      try {
        const formDataToSave = {
          event_type: eventFormData.eventType,
          location: eventFormData.location,
          guest_count: parseInt(eventFormData.guestCount),
          date: new Date(eventFormData.date)
        };
        
        await formService.createForm(formDataToSave);
        console.log('Event form data saved successfully');
      } catch (error) {
        console.error('Error saving event form data:', error);
        toast.error('Error saving event details');
      }
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response =
        loginMethod === 'email'
          ? await authService.login(
              { email: formData.email, password: formData.password },
              dispatch
            )
          : await authService.loginWithPhone(
              { phone_no: formData.phoneNumber, password: formData.password },
              dispatch
            );

      if (response.success) {
        toast.dismiss();
        toast.success('Login successful!');
        dispatch(fetchWishlist());
        await saveEventFormData();
      } else {
        if (response.unverifiedEmail) {
          toast.dismiss();
          toast.error('Please verify your email/phone before logging in');
        } else {
          toast.dismiss();
          toast.error(response.message || 'Login failed');
        }
      }
    } catch (error) {
      toast.dismiss();
      toast.error(error.message || 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setGoogleLoading(true);
    try {
      const response = await authService.continueWithGoogle(
        credentialResponse.credential,
        dispatch
      );
      if (response.success) {
        toast.dismiss();
        toast.success('Google login successful!');
        dispatch(fetchWishlist());
      } else {
        toast.dismiss();
        toast.error(response.message);
      }
    } catch (error) {
      toast.dismiss();
      toast.error('Google authentication failed');
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await authService.forgotPasswordMobile({
        phone_no: formData.phoneNumber,
      });
      if (response.success) {
        toast.dismiss();
        toast.success('OTP sent successfully!');
        setShowOTPVerification(true);
        setTimeLeft(600);
      } else {
        throw new Error(response.message);
      }
    } catch (error) {
      toast.dismiss();
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (formData.newPassword !== formData.confirmPassword) {
      toast.dismiss();
      toast.error("Passwords don't match");
      return;
    }

    setLoading(true);
    try {
      const response = await authService.resetPasswordMobile({
        phone_no: formData.phoneNumber,
        otp,
        newPassword: formData.newPassword,
      });

      if (response.success) {
        toast.dismiss();
        toast.success('Password reset successful!');
        setShowForgotPassword(false);
        setShowOTPVerification(false);
        setFormData((prev) => ({
          ...prev,
          showResetPassword: false,
          newPassword: '',
          confirmPassword: '',
        }));
      } else {
        throw new Error(response.message);
      }
    } catch (error) {
      toast.dismiss();
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) {
      toast.dismiss();
      toast.error('Please enter a valid 6-digit OTP');
      return;
    }

    setLoading(true);
    try {
      const response = await authService.verifyResetOTP({
        phone_no: formData.phoneNumber,
        otp,
      });
      if (response.success) {
        toast.dismiss();
        toast.success('OTP verified successfully!');
        setFormData((prev) => ({ ...prev, showResetPassword: true }));
      } else {
        throw new Error(response.message);
      }
    } catch (error) {
      toast.dismiss();
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (timeLeft > 0) return;
    setLoading(true);
    try {
      const response = await authService.resendForgotPasswordOTP({
        phone_no: formData.phoneNumber,
      });
      if (response.success) {
        toast.dismiss();
        toast.success('OTP resent successfully!');
        setTimeLeft(600);
      } else {
        throw new Error(response.message);
      }
    } catch (error) {
      toast.dismiss();
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  if (showForgotPassword) {
    if (showOTPVerification && !formData.showResetPassword) {
      return (
        <div className='text-center'>
          <h2 className='text-2xl font-bold mb-2'>Verify OTP</h2>
          <p className='text-gray-600 mb-6'>
            Enter the OTP sent to {formData.phoneNumber}
          </p>

          <form onSubmit={handleVerifyOTP} className='space-y-4'>
            <div className='text-left'>
              <label className='block text-sm font-medium mb-1'>
                Enter OTP
              </label>
              <input
                type='text'
                placeholder='Enter 6-digit OTP'
                className='w-full p-2 rounded-xl border border-gray-300 focus:ring-2 focus:ring-textprimary focus:outline-none'
                value={otp}
                onChange={(e) =>
                  setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))
                }
                maxLength={6}
                disabled={loading}
              />
            </div>

            <div className='text-center text-sm'>
              {timeLeft > 0 ? (
                <p className='text-gray-600'>
                  Resend OTP in {formatTime(timeLeft)}
                </p>
              ) : (
                <button
                  type='button'
                  onClick={handleResendOTP}
                  className='text-red-500 hover:text-red-600'
                  disabled={loading}
                >
                  Resend OTP
                </button>
              )}
            </div>

            <button
              type='submit'
              className='w-full bg-buttonprimary text-white py-2 rounded-xl hover:bg-buttonhoverPrimary transition-colors disabled:opacity-50'
              disabled={loading}
            >
              {loading ? 'Verifying...' : 'Verify OTP'}
            </button>

            <button
              type='button'
              onClick={() => setShowOTPVerification(false)}
              className='w-full text-gray-600 hover:text-red-500 text-sm'
              disabled={loading}
            >
              Back
            </button>
          </form>
        </div>
      );
    }

    if (formData.showResetPassword) {
      return (
        <div className='text-center'>
          <h2 className='text-2xl font-bold mb-2'>Reset Password</h2>
          <p className='text-gray-600 mb-6'>Enter your new password</p>

          <form onSubmit={handleResetPassword} className='space-y-4'>
            <div className='text-left'>
              <label className='block text-sm font-medium mb-1'>
                New Password
              </label>
              <input
                type='password'
                placeholder='Enter new password'
                className='w-full p-2 rounded-xl border border-gray-300 focus:ring-2 focus:ring-textprimary focus:outline-none'
                value={formData.newPassword}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    newPassword: e.target.value,
                  }))
                }
                disabled={loading}
              />
            </div>

            <div className='text-left'>
              <label className='block text-sm font-medium mb-1'>
                Confirm New Password
              </label>
              <input
                type='password'
                placeholder='Confirm new password'
                className='w-full p-2 rounded-xl border border-gray-300 focus:ring-2 focus:ring-textprimary focus:outline-none'
                value={formData.confirmPassword}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    confirmPassword: e.target.value,
                  }))
                }
                disabled={loading}
              />
            </div>

            <button
              type='submit'
              className='w-full bg-buttonprimary text-white py-2 rounded-xl hover:bg-buttonhoverPrimary transition-colors disabled:opacity-50'
              disabled={loading}
            >
              {loading ? 'Resetting Password...' : 'Reset Password'}
            </button>
          </form>
        </div>
      );
    }

    return (
      <div className='text-center'>
        <h2 className='text-2xl font-bold mb-2'>Reset Password</h2>
        <p className='text-gray-600 mb-6'>
          Enter your phone number to receive OTP
        </p>

        <form onSubmit={handleForgotPassword} className='space-y-4'>
          <div className='text-left'>
            <label className='block text-sm font-medium mb-1'>
              Phone Number
            </label>
            <input
              type='tel'
              placeholder='Enter 10-digit phone number'
              className='w-full p-2 rounded-xl border border-gray-300 focus:ring-2 focus:ring-textprimary focus:outline-none'
              value={formData.phoneNumber}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  phoneNumber: e.target.value.replace(/\D/g, '').slice(0, 10),
                }))
              }
              maxLength={10}
              disabled={loading}
            />
          </div>

          <button
            type='submit'
            className='w-full bg-buttonprimary text-white py-2 rounded-xl hover:bg-buttonhoverPrimary transition-colors disabled:opacity-50'
            disabled={loading}
          >
            {loading ? 'Sending OTP...' : 'Get OTP'}
          </button>

          <button
            type='button'
            onClick={() => setShowForgotPassword(false)}
            className='w-full text-gray-600 hover:text-textDarkPrimary text-sm'
            disabled={loading}
          >
            Back to Login
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className='text-center'>
      <h2 className='text-2xl text-textprimary font-bold mb-2'>Welcome back</h2>
      <p className='text-gray-600 mb-6'>
        Welcome back! Please enter your details.
      </p>

      <div className='flex justify-center mb-6'>
        <button
          onClick={() => {
            setLoginMethod((prev) => (prev === 'email' ? 'mobile' : 'email'));
            setFormData((prev) => ({
              ...prev,
              email: '',
              phoneNumber: '',
              password: '',
            }));
          }}
          className='text-sm text-textprimary hover:text-textprimary/80 underline'
          disabled={loading}
        >
          Login with {loginMethod === 'email' ? 'mobile number' : 'email'}{' '}
          instead
        </button>
      </div>

      <form onSubmit={handleLogin} className='space-y-3'>
        {loginMethod === 'email' ? (
          <div className='text-left'>
            <label className='block text-sm font-medium mb-1'>
              Email Address
            </label>
            <input
              type='email'
              placeholder='Enter your email'
              className='w-full
               p-2 rounded-xl border-2 font-normal  border-[#A9BAD4] focus:ring-2 focus:ring-textprimary focus:outline-none'
              value={formData.email}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, email: e.target.value }))
              }
              disabled={loading}
            />
          </div>
        ) : (
          <div className='text-left'>
            <label className='block text-sm font-medium mb-1'>
              Phone Number
            </label>
            <input
              type='tel'
              placeholder='Enter 10-digit phone number'
              className='w-full p-2 rounded-xl               
 border-2 border-[#A9BAD4] focus:ring-2 font-normal focus:ring-textprimary focus:outline-none'
              value={formData.phoneNumber}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  phoneNumber: e.target.value.replace(/\D/g, '').slice(0, 10),
                }))
              }
              maxLength={10}
              disabled={loading}
            />
          </div>
        )}

        <div className='text-left'>
          <label className='block text-sm font-medium mb-1'>Password</label>
          <input
            type='password'
            placeholder='Enter your password'
            className='w-full            
 p-2 rounded-xl border-2 border-[#A9BAD4] font-normal focus:ring-2 focus:ring-textprimary focus:outline-none'
            value={formData.password}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, password: e.target.value }))
            }
            disabled={loading}
          />
        </div>

        <div className='text-right'>
          {loginMethod === 'email' ? (
            <a
              href='/forget-password'
              className='text-sm text-gray-600 hover:text-textprimary/70'
            >
              Forgot password?
            </a>
          ) : (
            <button
              type='button'
              onClick={() => setShowForgotPassword(true)}
              className='text-sm text-gray-600 hover:text-textprimary'
              disabled={loading}
            >
              Forgot password?
            </button>
          )}
        </div>

        <button
          type='submit'
          className='w-full bg-buttonprimary text-white py-2 rounded-xl hover:bg-buttonhoverPrimary transition-colors disabled:opacity-50'
          disabled={loading}
        >
          {loading ? (
            <span className='flex items-center justify-center'>
              <svg className='animate-spin h-5 w-5 mr-3' viewBox='0 0 24 24'>
                <circle
                  className='opacity-25'
                  cx='12'
                  cy='12'
                  r='10'
                  stroke='currentColor'
                  strokeWidth='4'
                />
                <path
                  className='opacity-75'
                  fill='currentColor'
                  d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z'
                />
              </svg>
              Logging in...
            </span>
          ) : (
            'Login'
          )}
        </button>

        <div className='text-center text-gray-500'>or</div>

        {googleLoading ? (
          <div className='flex justify-center items-center'>
            <div className='animate-spin rounded-full h-5 w-5 border-b-2 border-gray-900'></div>
          </div>
        ) : (
          <div className='w-full flex justify-center items-center'>
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={() => toast.error('Google authentication failed')}
              type='standard'
              theme='outline'
              size='large'
              text={googleLoading ? 'Processing...' : 'continue_with'}
              shape='rectangular'
              logo_alignment='left'
              width='100%'
            />
          </div>
        )}
      </form>

      <p className='mt-4 text-sm text-gray-600'>
        Don't have an account?{' '}
        <button
          onClick={onShowSignup}
          className='text-textprimary underline hover:text-textprimary/90'
          disabled={loading}
        >
          Sign up for free!
        </button>
      </p>
    </div>
  );
};

export default LoginForm;
